import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, PawPrint, Users, Stethoscope, Home } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import PartnerRegistrationDialog from '@/components/PartnerRegistrationDialog';

const LandingPage = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${searchQuery}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header data-testid="landing-header" className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <PawPrint className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>International Pet Registry</span>
          </div>
          <div className="flex gap-4 items-center">
            <LanguageSwitcher variant="minimal" />
            {user ? (
              <Button data-testid="dashboard-btn" onClick={() => navigate('/dashboard')} className="bg-primary hover:bg-primary/90 h-11 px-8 rounded-full">
                {t('nav.dashboard')}
              </Button>
            ) : (
              <Button data-testid="login-btn" onClick={() => navigate('/auth')} className="bg-primary hover:bg-primary/90 h-11 px-8 rounded-full">
                {t('nav.login')}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20" style={{
        backgroundImage: 'linear-gradient(180deg, rgba(26,77,46,0.8) 0%, rgba(26,77,46,0.4) 100%), url(https://images.unsplash.com/photo-1655761167947-cb93277c192c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMHdpdGglMjBhZG9wdGVkJTIwZG9nJTIwcGFya3xlbnwwfHx8fDE3NjYzMjA3ODd8MA&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container mx-auto px-6 text-center text-white">
          <h1 data-testid="hero-title" className="text-5xl md:text-7xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Track & Protect Your Pets
          </h1>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto leading-relaxed">
            The world's most comprehensive pet microchip registry. Connect shelters, vets, breeders, and owners in one unified platform.
          </p>
          
          {/* Search Bar */}
          <div data-testid="microchip-search" className="max-w-2xl mx-auto bg-white rounded-full p-2 flex gap-2 shadow-lg">
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Search by microchip number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 border-0 h-12 text-lg rounded-full focus:ring-0 text-stone-900 placeholder:text-stone-400"
            />
            <Button data-testid="search-btn" onClick={handleSearch} className="bg-accent hover:bg-accent/90 h-12 px-8 rounded-full">
              <Search className="w-5 h-5 mr-2" />
              Search
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16" style={{ fontFamily: 'Manrope, sans-serif' }}>Who We Serve</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div data-testid="shelter-card" className="bg-white border border-stone-100 rounded-2xl p-8 hover:border-primary/20 transition-all shadow-sm hover:shadow-lg cursor-pointer">
              <Home className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3">Animal Shelters</h3>
              <p className="text-stone-600 leading-relaxed">Manage adoptions, volunteers, and microchip registrations. Create custom forms and track financials.</p>
            </div>
            
            <div data-testid="breeder-card" className="bg-white border border-stone-100 rounded-2xl p-8 hover:border-primary/20 transition-all shadow-sm hover:shadow-lg cursor-pointer">
              <PawPrint className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3">Breeders</h3>
              <p className="text-stone-600 leading-relaxed">Track breeding records, manage sales, and maintain waitlists. Build your website and showcase available pets.</p>
            </div>
            
            <div data-testid="vet-card" className="bg-white border border-stone-100 rounded-2xl p-8 hover:border-primary/20 transition-all shadow-sm hover:shadow-lg cursor-pointer">
              <Stethoscope className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3">Veterinarians</h3>
              <p className="text-stone-600 leading-relaxed">Search patients by microchip, conduct video consultations, and manage prescriptions online.</p>
            </div>
            
            <div data-testid="owner-card" className="bg-white border border-stone-100 rounded-2xl p-8 hover:border-primary/20 transition-all shadow-sm hover:shadow-lg cursor-pointer">
              <Users className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-2xl font-semibold mb-3">Pet Owners</h3>
              <p className="text-stone-600 leading-relaxed">Register your pets, access medical records, and ensure they can always find their way home.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Join thousands of shelters, vets, breeders, and pet owners protecting pets worldwide.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button data-testid="cta-signup-btn" onClick={() => navigate('/auth')} className="bg-accent hover:bg-accent/90 h-14 px-12 text-lg rounded-full">
              Create Free Account
            </Button>
            <Button data-testid="lost-found-btn" onClick={() => navigate('/lost-and-found')} variant="outline" className="h-14 px-12 text-lg rounded-full border-2 border-white text-white hover:bg-white hover:text-primary">
              Lost & Found Pets
            </Button>
          </div>
        </div>
      </section>

      {/* Partner Network Section */}
      <section className="py-20 bg-stone-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Partner Network</h2>
            <p className="text-xl text-stone-600 max-w-2xl mx-auto">
              Connect your microchip registry to our global federated search network
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h3 className="font-semibold mb-2">Register Online</h3>
              <p className="text-stone-600 text-sm">Fill out your registry details and API endpoint</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">2</span>
              </div>
              <h3 className="font-semibold mb-2">Pay $20/month</h3>
              <p className="text-stone-600 text-sm">Secure payment via Stripe - instant activation</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h3 className="font-semibold mb-2">Go Live!</h3>
              <p className="text-stone-600 text-sm">Your registry is instantly connected to our network</p>
            </div>
          </div>
          
          <div className="flex gap-4 justify-center flex-wrap">
            <Button onClick={() => navigate('/partners')} variant="outline" className="h-12 px-8 rounded-full">
              <Users className="w-5 h-5 mr-2" />
              View Connected Partners
            </Button>
            <Button onClick={() => setShowPartnerDialog(true)} className="bg-primary hover:bg-primary/90 h-12 px-8 rounded-full" id="become-partner-btn">
              Become a Partner - $20/month
            </Button>
          </div>
        </div>
      </section>

      {/* Partner Registration Dialog */}
      <PartnerRegistrationDialog 
        open={showPartnerDialog} 
        onOpenChange={setShowPartnerDialog} 
      />

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-12">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <PawPrint className="w-6 h-6" />
            <span className="text-xl font-bold">International Pet Registry</span>
          </div>
          <p>© 2025 International Pet Registry. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
